// ===== Imports =====
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReduxState} from '../../store/types';
import * as Yup from 'yup';
import {Formik} from 'formik';
import AiForm from "../Forms/Extensions/AiForm";
import {actions} from "../../store";
import {ServiceFeatureAttribute, ServiceFeatureName} from "../../store/types/ServiceFeature";
import {YesNo} from "../../store/types/CallScreening";
import {AiFormType} from "../Forms/Extensions/AiForm.utils";
import {AiTabInterface} from "./AiTab.utils";

// ===== Main Component =====
const AiTab: React.FC<AiTabInterface> = ({
    handleInitDataChange,
    handleSubmitForm,
    handleDirtyChange,
    handleSetSubmitFunc,
    handleSetIsValidFunc,
    sessionToken
}) => {
    // ===== Hooks =====
    const dispatch = useDispatch();
    // Individual selectors to prevent unnecessary rerenders
    const token = useSelector((state: ReduxState) => state.aiAgent.token);
    const aiAgentsList = useSelector((state: ReduxState) => state.aiAgent.aiAgentsList);
    const aiAgentsListLoading = useSelector((state: ReduxState) => state.aiAgent.aiAgentsListLoading);
    const aiAgentsListLoadingError = useSelector((state: ReduxState) => state.aiAgent.aiAgentsListLoadingError);
    const tenantId = useSelector((state: ReduxState) => state.aiAgent.tenantId);
    const fetchTokenInProgress = useSelector((state: ReduxState) => state.aiAgent.fetchTokenInProgress);
    const tennatChecktatus = useSelector((state: ReduxState) => state.aiAgent.tennatChecktatus);
    const checkTenantInProgress = useSelector((state: ReduxState) => state.aiAgent.checkTenantInProgress);
    const features = useSelector((state: ReduxState) => state.extensions.serviceFeatures);

    const globalEmail = useSelector((state: ReduxState) => state.generic.globalCustomerInfo?.customer_info.email || null);
    const globalCountry = useSelector((state: ReduxState) => state.generic.globalCustomerInfo?.customer_info.country || null);
    const individualEmail = window.localStorage.getItem('user_email');

    // Guard ref to avoid repeated token exchange per sessionToken value
    const hasRequestedTokenExchangeRef = useRef<string | null>(null);

    // Track toggle from AiForm
    const [formAiEnabled, setFormAiEnabled] = useState<boolean | undefined>(undefined);

    // ===== Memoized Values =====
    const aiEnabledValue = useMemo(() => {
        const service = features?.find(el => el.name === ServiceFeatureName.SipStaticContact);
        return (service && service.effective_flag_value === YesNo.Yes) || false;
    }, [features]);

    const initValues = useMemo(() => {
        const service = features && features.find(el => el.name === ServiceFeatureName.SipStaticContact)
        const aiEnabledValue = service && service?.effective_flag_value === YesNo.Yes || false
        let output = {
            init: {
                aiAgent: null,
                aiEnabled: aiEnabledValue
            } as AiFormType,
            data: {
                aiAgentService: null,
                sipService: null,
            }
        }
        if (service) {
            const host = service.attributes.find((el: ServiceFeatureAttribute) => el.name === 'host')
            const port = service.attributes.find((el: ServiceFeatureAttribute) => el.name === 'port')
            const user = service.attributes.find((el: ServiceFeatureAttribute) => el.name === 'user')
            const sipContact = `${user?.values[0]}@${host?.values[0]}:${port?.values[0]}`

            const aiAgentFromList = aiAgentsList &&
            aiAgentsList?.agents.length > 0 ? aiAgentsList.agents.find(el => el.sip_uri === sipContact && el.agent_id != 'unknown') : null;

            output = {
                init: {
                    aiAgent: aiAgentFromList ?? null,
                    aiEnabled: (aiAgentFromList && aiEnabledValue) || false
                },
                data: {
                    // @ts-ignore
                    aiAgentService: aiAgentFromList,
                    // @ts-ignore
                    sipService: service,
                }
            }
        }
        return output
    }, [features, aiAgentsList]);

    // Derive effective aiEnabled: form toggle takes priority, then service feature flag.
    // Using aiEnabledValue (feature flag alone) rather than initValues.init.aiEnabled avoids
    // a chicken-and-egg deadlock: initValues.init.aiEnabled is false until aiAgentsList loads,
    // but aiAgentsList only loads when effectiveAiEnabled is true.
    const effectiveAiEnabled = formAiEnabled ?? aiEnabledValue;

    const onAiEnabledChange = useCallback((enabled: boolean) => {
        setFormAiEnabled(enabled);
    }, []);

    // Comprehensive data loading check - gated on effectiveAiEnabled
    const isDataLoading = useMemo(() => {
        if (!effectiveAiEnabled) return false;
        // Token being fetched
        if (fetchTokenInProgress) return true;
        // Session token available but adapter token not yet fetched
        if (sessionToken && !token && fetchTokenInProgress === undefined) return true;
        // Token available but tenant not checked yet (only if prerequisites exist)
        if (token && tennatChecktatus === undefined && globalEmail && globalCountry) return true;
        // Tenant check in progress
        if (checkTenantInProgress) return true;
        // Tenant exists but agents list not yet loaded
        if (tenantId && aiAgentsList === undefined && aiAgentsListLoadingError === undefined) return true;
        // Agents list loading
        return aiAgentsListLoading;

    }, [effectiveAiEnabled, fetchTokenInProgress, sessionToken, token, tennatChecktatus, globalEmail, globalCountry, checkTenantInProgress, tenantId, aiAgentsList, aiAgentsListLoadingError, aiAgentsListLoading]);

    // ===== Effects =====
    useEffect(() => {
        if (
            effectiveAiEnabled &&
            sessionToken &&
            !token &&
            !fetchTokenInProgress &&
            hasRequestedTokenExchangeRef.current !== sessionToken
        ) {
            hasRequestedTokenExchangeRef.current = sessionToken;
            dispatch(actions.exchangeTokenWithInternalAdapter.request({
                // @ts-ignore
                portaBillingSessionToken: sessionToken
            }));
        }
    }, [effectiveAiEnabled, sessionToken, token, fetchTokenInProgress, dispatch]);

    useEffect(() => {
        if (
            effectiveAiEnabled &&
            globalEmail &&
            globalCountry &&
            (tennatChecktatus === undefined || tennatChecktatus === 'existing') &&
            fetchTokenInProgress === false &&
            !tenantId &&
            token?.access_token
        ) {
            dispatch(actions.checkTenant.request());
        }
    }, [effectiveAiEnabled, tenantId, tennatChecktatus, fetchTokenInProgress, token, dispatch, globalEmail, globalCountry]);

    useEffect(() => {
        if (effectiveAiEnabled && tenantId && token && aiAgentsList === undefined && aiAgentsListLoadingError === undefined && !aiAgentsListLoading) {
            dispatch(actions.getAiAgentsList.request({
                limit: 50,
                offset: 0
            }));
        }
    }, [effectiveAiEnabled, token, tenantId, aiAgentsList, aiAgentsListLoading, aiAgentsListLoadingError, dispatch]);


    const reqEmailExist = useMemo(() => {
        const hasEmail = (globalEmail && globalEmail.length > 0) || (individualEmail && individualEmail.length > 0);
        return hasEmail ? true : null;
    }, [globalEmail, individualEmail])

    const reqCountryExist = useMemo(() => {
        return  globalCountry && globalCountry.length > 0 || false
    }, [globalCountry]);

    // ===== Validation Schema =====
    const aiFormValidationSchema = () => Yup.object().shape({
        aiAgent: Yup.object().when('aiEnabled', {
            is: true,
            then: Yup.object().required('This field is required').nullable(),
            otherwise: Yup.object().nullable(),
        }),
        aiEnabled: Yup.boolean().required(),
    })

    // ===== Init Form Values Effect =====
    useEffect(() => {
        if (!isDataLoading && initValues.init) {
            handleInitDataChange?.(initValues.init);
            handleSubmitForm?.(initValues.init);
        }
    }, [isDataLoading, initValues, handleInitDataChange, handleSubmitForm]);

    // ===== Render =====
    return (
        <Formik<AiFormType>
            initialValues={initValues.init}
            onSubmit={(form) => handleSubmitForm?.(form)}
            validationSchema={aiFormValidationSchema}
            validateOnChange={false}
            enableReinitialize
        >
            {() => (
                <AiForm
                    emailIsPresent={reqEmailExist}
                    countryIsPresent={reqCountryExist}
                    tenantId={tenantId}
                    isDataLoading={isDataLoading}
                    onAiEnabledChange={onAiEnabledChange}
                    handleSubmitForm={handleSubmitForm}
                    handleDirtyChange={handleDirtyChange}
                    handleSetSubmitFunc={handleSetSubmitFunc}
                    handleSetIsValidFunc={handleSetIsValidFunc}
                />
            )}
        </Formik>
    );
};

// ===== Export =====
export default AiTab;
